import React from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  CardMedia, 
  CircularProgress,
  Button,
  Divider
} from '@mui/material';
import { RefreshRounded } from '@mui/icons-material';
import { useSpotify } from '../context';
import Layout from '../components/layout/Layout';
import { useNavigate } from 'react-router-dom';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { 
    recentlyPlayed, 
    topTracks, 
    isLoading, 
    error, 
    refreshRecentlyPlayed, 
    refreshTopItems 
  } = useSpotify();

  const handleRefresh = async () => {
    await Promise.all([
      refreshRecentlyPlayed(),
      refreshTopItems()
    ]);
  };

  const renderTrackCard = (track: any, index: number) => (
    <Box 
      key={`${track.id}-${index}`} 
      className="track-card"
      sx={{ 
        width: { 
          xs: '100%', 
          sm: '50%', 
          md: '33.33%', 
          lg: '25%' 
        }, 
        padding: 1 
      }}
    >
      <Card 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          height: '100%',
          cursor: 'pointer',
          '&:hover': {
            boxShadow: 6,
            transform: 'translateY(-4px)',
            transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out'
          }
        }}
        onClick={() => navigate(`/track/${track.id}`)}
      >
        {track.imageUrl ? (
          <CardMedia
            component="img"
            height="160"
            image={track.imageUrl}
            alt={track.name}
          />
        ) : (
          <Box 
            className="placeholder-album"
            sx={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <Typography variant="body2" color="text.secondary">Album</Typography>
          </Box>
        )}
        <CardContent sx={{ flexGrow: 1 }}>
          <Typography variant="subtitle1" component="div" noWrap>
            {track.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {track.artists.map((artist: any) => artist.name).join(', ')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {track.album.name}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );

  return (
    <Layout>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" component="h1">
            Home
          </Typography>
          <Button 
            startIcon={<RefreshRounded />} 
            onClick={handleRefresh}
            disabled={isLoading}
          >
            Refresh
          </Button>
        </Box>
        
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" sx={{ my: 2 }}>
            {error}
          </Typography>
        ) : (
          <>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ mb: 2 }}>
                Recently Played
              </Typography>
              {recentlyPlayed.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', mx: -1 }}>
                  {recentlyPlayed.slice(0, 8).map((track, index) => renderTrackCard(track, index))}
                </Box>
              ) : (
                <Typography variant="body1" color="text.secondary">
                  No recently played tracks found.
                </Typography>
              )}
            </Box>
            
            <Divider sx={{ my: 4 }} />
            
            <Box>
              <Typography variant="h5" sx={{ mb: 2 }}>
                Your Top Tracks
              </Typography>
              {topTracks.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', mx: -1 }}>
                  {topTracks.slice(0, 8).map((track, index) => renderTrackCard(track, index))}
                </Box>
              ) : (
                <Typography variant="body1" color="text.secondary">
                  No top tracks found.
                </Typography>
              )}
            </Box>
          </>
        )}
      </Box>
    </Layout>
  );
};

export default Home;
